import { Dispatch, FC, SetStateAction } from 'react';
import moment from 'moment';
import 'moment/locale/pt-br';
import DatePicker, { registerLocale } from 'react-datepicker';
import { ptBR } from 'date-fns/locale/pt-BR';
import 'react-datepicker/dist/react-datepicker.css';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import { useAuth } from '../../providers/AuthProvider';
import './InputDateRange.scss';

registerLocale('pt-BR', ptBR);

const parseDate = (date: any): Date | null => {
    if (!date) return null;
    if (date instanceof Date) return date;
    const m = moment(date, 'DD/MM/YYYY', true);
    return m.isValid() ? m.toDate() : null;
};

interface InputDateRangeProps {
    startDate: any;
    setStartDate: Dispatch<SetStateAction<any>>;
    endDate: any;
    setEndDate: Dispatch<SetStateAction<any>>;
    startLabelText?: string;
    endLabelText?: string;
    errors?: any;
    disableStartDate?: boolean;
    disableEndDate?: boolean;
}

const InputDateRange: FC<InputDateRangeProps> = ({
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    startLabelText,
    endLabelText,
    errors,
    disableEndDate = false,
    disableStartDate = false,
}) => {
    const { onInvalid } = useAuth();

    const parsedStart = parseDate(startDate);
    const parsedEnd = parseDate(endDate);

    const buildHighlightDates = (): { [className: string]: Date[] }[] => {
        if (!parsedStart || !parsedEnd) return [];
        const highlights: { [className: string]: Date[] }[] = [
            { 'react-datepicker__day--range-start': [parsedStart] },
        ];
        const inRange: Date[] = [];
        const cursor = new Date(parsedStart);
        cursor.setDate(cursor.getDate() + 1);
        while (cursor < parsedEnd) {
            inRange.push(new Date(cursor));
            cursor.setDate(cursor.getDate() + 1);
        }
        if (inRange.length) highlights.push({ 'react-datepicker__day--in-range': inRange });
        if (parsedStart.getTime() !== parsedEnd.getTime()) {
            highlights.push({ 'react-datepicker__day--range-end': [parsedEnd] });
        }
        return highlights;
    };

    const handleStartChange = (date: Date | null) => {
        const formatted = date ? moment(date).format('DD/MM/YYYY') : '';
        setStartDate(formatted);
        if (date && parsedEnd && date > parsedEnd) {
            setEndDate(formatted);
        }
    };

    const handleEndChange = (date: Date | null) => {
        const formatted = date ? moment(date).format('DD/MM/YYYY') : '';
        setEndDate(formatted);
        if (date && parsedStart && date < parsedStart) {
            setStartDate(formatted);
        }
    };

    const highlights = buildHighlightDates();

    return (
        <div className="row position-relative input-daterange datepicker InputDateRange" data-testid="InputDateRange">
            <div className='col-sm-6 col-xs-12'>
                <label className="form-control-label">
                    {startLabelText ? startLabelText : 'Período Inicial'}
                </label>
                <div className='form-group'>
                    <DatePicker
                        locale="pt-BR"
                        dateFormat="dd/MM/yyyy"
                        selected={parsedStart}
                        onChange={handleStartChange}
                        startDate={parsedStart}
                        endDate={parsedEnd}
                        highlightDates={highlights}
                        disabled={disableStartDate}
                        placeholderText="Período Inicial"
                        name="startDate"
                        autoComplete="off"
                        className={`form-control ${onInvalid(errors?.startDate)}`}
                        wrapperClassName="w-100"
                        popperPlacement="bottom-start"
                    />
                    <ErrorMessage name="Período Inicial" type={errors?.startDate?.type} />
                </div>
            </div>
            <div className='col-sm-6 col-xs-12'>
                <div className='form-group'>
                    <label className="form-control-label">
                        {endLabelText ? endLabelText : 'Período Final'}
                    </label>
                    <DatePicker
                        locale="pt-BR"
                        dateFormat="dd/MM/yyyy"
                        selected={parsedEnd}
                        onChange={handleEndChange}
                        startDate={parsedStart}
                        endDate={parsedEnd}
                        minDate={parsedStart ?? undefined}
                        highlightDates={highlights}
                        disabled={disableEndDate}
                        placeholderText="Período Final"
                        name="endDate"
                        autoComplete="off"
                        className={`form-control ${onInvalid(errors?.endDate)}`}
                        wrapperClassName="w-100"
                        popperPlacement="bottom-end"
                    />
                    <ErrorMessage name="Período Final" type={errors?.endDate?.type} />
                </div>
            </div>
        </div>
    );
};

export default InputDateRange;
